// SearchPalette: the workspace's one search as the board's finder. The box
// holds the search's clauses and a field to find with; typing finds the
// groups, people, agents, kinds of work, servers, skills, tools, machines and
// models in what the search reaches, each with how many sessions it would
// leave and where review left them. Moving through them lights each one's
// flow on the board behind, so a choice is seen before it is made. Enter
// narrows to it and the finder stays open to keep narrowing; Shift+Enter
// leaves it out; Cmd+Enter starts a workspace from the search. A field and a
// value (file:deploy.yaml) or plain words are a clause as typed. Opened with
// nothing typed, it offers what was flagged more than usual, recent searches,
// and the fields to search by.
import { useEffect, useMemo, useRef, useState } from "react";
import { nodeSpan, parseQuery, resolveField } from "../lib/queryLang.js";
import { pipelineFilterText } from "../lib/queryPipeline.js";
import { sessionClauses } from "../lib/sessionMatch.js";
import { appendClause, setWorkspacePicks, togglePick, useWorkspacePicks } from "../lib/workspaceSearch.js";
import { reviewWords, splitReview } from "../lib/reviewClause.js";
import { useKeyedReads } from "../lib/keyedReads.js";
import { SEVERITY_COLOR } from "../lib/traceReview.js";
import { countText } from "./WorkspaceAddOverlay.jsx";
import { EYEBROW_CLASS } from "../lib/ui.js";

const MET = SEVERITY_COLOR.danger;
const FLAGGED = SEVERITY_COLOR.warn;
const NUM = new Intl.NumberFormat();
const RECENT_KEY = "origin-workspace-recent-searches-v1";
const PER_KIND = 5;
const CLASS_OF = { met: "met", not_verified: "flagged", not_met: "flagged", set_aside: "flagged" };
const MOD = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "") ? "Cmd" : "Ctrl";
// The box's height: what lays the box out elsewhere keeps this much room.
export const FINDER_BOX_H = 48;

// The words a clause's field is called by here, and the fields offered to
// search by, each with what it finds.
const FIELD_WORD = { group: "group", identity: "person", agent: "agent", topic: "work", mcp_server: "MCP", skill: "skill", tool: "tool", endpoint: "machine", model: "model", file: "file", text: "text", session: "trace", memory: "memory" };
const BY = [
  ["group", "a group, by department"],
  ["person", "a person, by name or email"],
  ["agent", "an agent"],
  ["topic", "a kind of work"],
  ["mcp", "an MCP server"],
  ["skill", "a skill"],
  ["tool", "a tool"],
  ["file", "a file it touched"],
  ["machine", "a machine, by hostname"],
  ["model", "a model"],
];

const readRecent = () => {
  try {
    const list = JSON.parse(globalThis.localStorage?.getItem(RECENT_KEY) || "[]");
    return Array.isArray(list) ? list.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
};
const remember = (text) => {
  const t = String(text || "").trim();
  if (!t) return;
  try {
    globalThis.localStorage?.setItem(RECENT_KEY, JSON.stringify([t, ...readRecent().filter((x) => x !== t)].slice(0, 6)));
  } catch {
    // Recent searches are this browser's convenience; without storage there are none.
  }
};

// A clause as the reader wrote it, from its place in the search: with the
// NOT and the brackets around it that its parsed span leaves off.
function writtenAt(text, node) {
  const span = nodeSpan(node);
  if (!span) return null;
  let [s, e] = span;
  for (let more = true; more;) {
    more = false;
    if (text[s - 1] === "(" && text[e] === ")") {
      s -= 1;
      e += 1;
      more = true;
    }
    let t = s;
    while (t > 0 && /\s/.test(text[t - 1])) t -= 1;
    if (t >= 3 && text.slice(t - 3, t).toUpperCase() === "NOT" && (t === 3 || /[\s(]/.test(text[t - 4]))) {
      s = t - 3;
      more = true;
    }
  }
  return text.slice(s, e);
}

// The search's clauses as pills: each in its positive form, whether it is
// left out, and the search written again without one of them, the rest in
// the reader's own words.
export function clausesOf(query) {
  const whole = pipelineFilterText(query);
  const pipe = query.slice(whole.length);
  // Where review left a session is not the query language's to say
  // (lib/reviewClause.js): its clauses are pills of their own after the rest,
  // in the board's own words.
  const { rest: filter, tokens: review } = splitReview(whole);
  const { clauses, errors } = sessionClauses(filter);
  if (errors.length) {
    const t = query.trim();
    return { pills: t ? [{ text: t, negated: false, field: null, value: t }] : [], without: () => "" };
  }
  const { ast } = parseQuery(filter);
  const own = (!ast ? [] : ast.type === "and" ? ast.children : [ast]).filter((n) => n.type !== "error").map((n) => writtenAt(filter, n));
  // Where the parse and the clauses do not line up, each written again.
  const written = own.length === clauses.length && own.every(Boolean) ? own : clauses.map((c) => {
    if (!c.negated) return / OR /.test(c.text) ? `(${c.text})` : c.text;
    return /\s/.test(c.text.replace(/"[^"]*"/g, "")) ? `NOT (${c.text})` : `-${c.text}`;
  });
  const pills = [
    ...clauses.map((c) => {
      const m = /^([a-z_]+)(?::|!=|>=|<=|>|<)/i.exec(c.text);
      const key = m ? resolveField(m[1])?.key : null;
      return { ...c, field: key ? FIELD_WORD[key] || m[1] : null, value: m ? c.label : c.text };
    }),
    ...review.map((k) => ({ text: k.text, negated: k.negated, label: reviewWords(k.places), field: "review", value: reviewWords(k.places) })),
  ];
  const all = [...written, ...review.map((k) => k.raw)];
  return { pills, without: (i) => `${all.filter((_, j) => j !== i).join(" ")}${pipe}`.trim() };
}

// The search without a clause it holds (as the search reads it), else as it
// was: a card or a row picked again widens the search back out of it.
export function withoutClause(query, clause) {
  const review = splitReview(clause).tokens[0];
  const want = review ? { text: review.text, negated: review.negated } : sessionClauses(clause).clauses[0];
  if (!want) return query;
  const { pills, without } = clausesOf(query);
  const i = pills.findIndex((p) => p.text === want.text && p.negated === want.negated);
  return i < 0 ? query : without(i);
}

// What was typed: a field and a value to find among that field's values
// (person:len, -agent:cod), or words to find among every value.
export function parseTerm(raw) {
  const m = /^(-?)([a-z_]+)(:|!=)(.*)$/i.exec(raw);
  const key = m ? resolveField(m[2])?.key || null : null;
  const find = (m && key ? m[4] : raw).replace(/^"|"$/g, "").toLowerCase();
  return { raw, key, find, leaveOut: !!m && !!key && (m[1] === "-" || m[3] === "!=") };
}

// Every value in reach, with how many sessions it has and how review left
// them: the finder's rows.
function useIndex(kinds, sessions, clauseFor) {
  return useMemo(() => {
    const rows = [];
    for (const kind of kinds) {
      const by = new Map();
      for (const s of sessions) {
        const vs = kind.of(s);
        const c = CLASS_OF[s.outcome];
        for (const v of new Set(Array.isArray(vs) ? vs : [vs])) {
          if (!v) continue;
          let e = by.get(v);
          if (!e) by.set(v, (e = { sessions: 0, met: 0, flagged: 0 }));
          e.sessions += 1;
          if (c) e[c] += 1;
        }
      }
      for (const [v, e] of by) {
        const clause = kind.def && clauseFor ? clauseFor(kind.def, v) : kind.clause(v);
        if (!clause) continue;
        const name = String(kind.name(v));
        rows.push({ key: `${kind.label}:${v}`, kind, value: v, name, lower: name.toLowerCase(), clause, fieldKey: resolveField(kind.field)?.key, ...e });
      }
    }
    return rows;
  }, [kinds, sessions, clauseFor]);
}

function Highlight({ text, term }) {
  const at = term ? text.toLowerCase().indexOf(term) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-medium text-[color:var(--ink)]">{text.slice(at, at + term.length)}</span>
      {text.slice(at + term.length)}
    </>
  );
}

// A row's sessions against the largest row in view, banded by where review
// left them, as the board's strands are.
function ShareBar({ row, max }) {
  const share = Math.max(0.03, Math.min(1, row.sessions / Math.max(1, max)));
  return (
    <span className="flex h-[4px] w-full overflow-hidden rounded-[1px] bg-[color:var(--line-soft)]" aria-hidden="true">
      <span className="flex h-full overflow-hidden" style={{ width: `${share * 100}%` }}>
        {row.met ? <span style={{ width: `${(row.met / row.sessions) * 100}%`, minWidth: 2, background: MET }} /> : null}
        {row.flagged ? <span style={{ width: `${(row.flagged / row.sessions) * 100}%`, minWidth: 2, background: FLAGGED }} /> : null}
        <span className="flex-1 bg-[color:var(--muted)] opacity-60" />
      </span>
    </span>
  );
}

export const Glass = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="7" cy="7" r="4.75" />
    <path d="M10.5 10.5 14 14" strokeLinecap="square" />
  </svg>
);
// The box holds as many pills as fit beside room to type, each cutting its
// value to fit (but for its first letters); the oldest clauses beyond that wait
// behind one "+N" pill, opening onto them. Nothing is scrolled out of sight.
const PILL_MIN = 120;
const PICKS_W = 160;
const MORE_W = 48;
const TYPE_MIN = 140;
export function foldedCount({ width, clauses, picks }) {
  if (!width || !clauses) return 0;
  const room = width - TYPE_MIN - (picks ? PICKS_W : 0);
  if (clauses * PILL_MIN <= room) return 0;
  return clauses - Math.max(0, Math.floor((room - MORE_W) / PILL_MIN));
}
const PILL = "inline-flex h-[26px] items-center gap-[5px] overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] pl-[8px] pr-[2px] text-[12px]";
const PILL_X = "grid h-[20px] w-[18px] shrink-0 place-items-center text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]";

const Key = ({ children }) => <span className="mr-[5px] rounded-[2px] border border-[color:var(--line)] px-[4px] text-[color:var(--ink)]">{children}</span>;
const counted = (n, one, many = `${one}s`) => `${NUM.format(n)} ${n === 1 ? one : many}`;

// The reading under way, as the app's placeholders say it.
function LoadingNote({ children }) {
  return (
    <span className="inline-flex items-center gap-[6px] text-[color:var(--muted)]" aria-live="polite">
      <span className="h-[6px] w-[6px] animate-pulse rounded-full bg-[color:var(--green)]" aria-hidden="true" />
      {children}
    </span>
  );
}

// What the search reaches, said in its box; while a row is picked, what
// narrowing to it would leave; while the board reads, that it is reading.
function Reach({ reach, reading, picked, total, wide }) {
  // In a narrow box the counts keep their numbers and drop words.
  if (reading) return <LoadingNote>Reading the traces</LoadingNote>;
  if (picked?.sessions != null) {
    return (
      <>
        <span><span className="font-medium text-[color:var(--ink)]">{NUM.format(picked.sessions)}</span><span className="text-[color:var(--muted)]">{` of ${counted(total, "trace")}`}</span></span>
        {picked.met ? <span style={{ color: MET }}>{`${NUM.format(picked.met)} met`}</span> : null}
        {picked.flagged ? <span style={{ color: FLAGGED }}>{`${NUM.format(picked.flagged)} flagged`}</span> : null}
      </>
    );
  }
  if (!reach) {
    return [76, 44, 58].map((w, i) => <span key={i} className="block h-[10px] self-center rounded-[1px] bg-[color:var(--line-soft)]" style={{ width: w, opacity: i ? 0.45 : 0.7 }} aria-hidden="true" />);
  }
  return (
    <>
      <span className="font-medium text-[color:var(--ink)]">{counted(reach.sessions, "trace")}</span>
      {wide ? <span className="text-[color:var(--muted)]">{counted(reach.machines, "machine")}</span> : null}
      {wide ? <span className="text-[color:var(--muted)]">{`${countText(reach.requests)} requests`}</span> : null}
      {reach.met ? <span style={{ color: MET }}>{`${NUM.format(reach.met)} met${wide ? " a standard" : ""}`}</span> : null}
      {reach.flagged ? <span style={{ color: FLAGGED }}>{`${NUM.format(reach.flagged)} ${reach.met && wide ? "more " : ""}flagged`}</span> : null}
    </>
  );
}

export default function SearchPalette({ query, setQuery, kinds, sessions, reach, reading = false, notice = null, clauseFor, range, width, finderWidth, finderHeight, onPreview, onStart, startLabel = "Start an investigation from this search", startShort = "Start an investigation", startHint = "start an investigation" }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  // Nothing is picked until something is typed or moved to, so opening the
  // finder leaves the board as it was.
  const [cursor, setCursor] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const pillsRef = useRef(null);
  const index = useIndex(kinds, sessions, clauseFor);
  // The group field only where the board has groups to find.
  const by = useMemo(() => BY.filter(([word]) => word !== "group" || kinds.some((k) => k.field === "group")), [kinds]);
  const total = sessions.length;
  const { pills, without } = useMemo(() => clausesOf(query), [query]);
  // The sessions picked into it (off the add sheet), held beside its clauses:
  // one pill for them, opening onto the list of them; and the clauses that do
  // not fit, behind one pill that opens onto them (`listing`).
  const [picks, setPicks] = useWorkspacePicks();
  const [listing, setListing] = useState(null);
  const listingRef = useRef(null);
  useEffect(() => {
    if (!listing) return undefined;
    const down = (e) => { if (!listingRef.current?.contains(e.target)) setListing(null); };
    window.addEventListener("pointerdown", down, true);
    return () => window.removeEventListener("pointerdown", down, true);
  }, [listing]);
  const [rowW, setRowW] = useState(0);
  useEffect(() => {
    const el = pillsRef.current;
    if (!el) return undefined;
    const read = () => setRowW(el.clientWidth);
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const folded = foldedCount({ width: rowW, clauses: pills.length, picks: picks.length > 0 });
  useEffect(() => {
    if ((listing === "picks" && picks.length < 2) || (listing === "clauses" && !folded)) setListing(null);
  }, [listing, picks.length, folded]);
  // A session picked before its title was read takes it once it is, or at
  // once when another list has read it already.
  const untitled = useMemo(() => picks.filter((x) => !x.title).map((x) => x.id), [picks]);
  const heads = useKeyedReads("getSessionHeads", untitled, { range });
  useEffect(() => {
    const read = heads.data;
    if (!read || !untitled.some((id) => read[id]?.summary)) return;
    setWorkspacePicks((list) => list.map((x) => (!x.title && read[x.id]?.summary ? { ...x, title: read[x.id].summary } : x)));
  }, [heads.data, untitled]);
  const searched = !!query.trim() || picks.length > 0;
  const recent = useMemo(() => (open ? readRecent() : []), [open]);
  const typed = useMemo(() => parseTerm(term.trim()), [term]);
  const base = useMemo(() => {
    let met = 0;
    let flagged = 0;
    for (const s of sessions) {
      if (CLASS_OF[s.outcome] === "met") met += 1;
      else if (CLASS_OF[s.outcome] === "flagged") flagged += 1;
    }
    return { met: met / Math.max(1, sessions.length), flagged: flagged / Math.max(1, sessions.length) };
  }, [sessions]);

  // What the finder offers. With nothing typed: the values whose sessions
  // review flagged at half again the usual rate or more, most flags over
  // the usual first (a met standard counting double), then recent searches. Typed: the values whose names hold it, a section
  // per kind (names that start with it, then what met a standard, first),
  // the fields it starts, and the text as a clause of its own. A field and
  // a value (person:len) finds that field's values, then the clause as
  // typed once it has a value.
  const sections = useMemo(() => {
    const out = [];
    const inQuery = new Set(pills.map((p) => p.text));
    if (!typed.raw) {
      const over = (r) => 2 * (r.met - r.sessions * base.met) + (r.flagged - r.sessions * base.flagged);
      const usual = base.met + base.flagged;
      const worth = index
        .filter((r) => r.sessions < total && !inQuery.has(r.clause) && over(r) > 0 && (r.met + r.flagged) / r.sessions >= 1.5 * usual)
        .sort((a, b) => over(b) - over(a))
        .slice(0, 7);
      if (worth.length) out.push({ label: "Flagged more than usual", mixed: true, rows: worth });
      const asked = recent.filter((r) => r !== query.trim()).slice(0, 4);
      if (asked.length) out.push({ label: "Recent searches", rows: asked.map((text) => ({ key: `recent:${text}`, recent: text, name: text })) });
      return out;
    }
    const pool = typed.key ? index.filter((r) => r.fieldKey === typed.key) : index;
    const hits = typed.find ? pool.filter((r) => r.lower.includes(typed.find)) : pool;
    const asTyped = { key: `raw:${typed.raw}`, raw: typed.raw, name: typed.raw };
    for (const kind of kinds) {
      const rows = hits
        .filter((r) => r.kind === kind)
        .sort((a, b) => (b.lower.startsWith(typed.find) - a.lower.startsWith(typed.find)) || b.met - a.met || b.flagged - a.flagged || b.sessions - a.sessions)
        .slice(0, typed.key ? 9 : PER_KIND);
      if (rows.length) out.push({ label: kind.label, rows });
    }
    if (typed.key) {
      if (typed.find) out.push({ label: "As typed", rows: [asTyped] });
    } else {
      const fields = by.filter(([word]) => word.startsWith(typed.find) || (typed.find.length > 1 && resolveField(typed.find)?.key === resolveField(word)?.key));
      if (fields.length) out.push({ label: "Search by", rows: fields.map(([word, says]) => ({ key: `field:${word}`, field: word, name: `${word}:`, says })) });
      out.push({ label: "As typed", rows: [{ ...asTyped, words: true }] });
    }
    return out;
  }, [typed, index, kinds, by, pills, recent, query, total, base]);
  const flat = useMemo(() => sections.flatMap((s) => s.rows), [sections]);
  const max = useMemo(() => Math.max(1, ...flat.map((r) => r.sessions || 0)), [flat]);
  const at = cursor < 0 ? -1 : Math.min(cursor, flat.length - 1);
  const picked = open && at >= 0 ? flat[at] || null : null;

  // The board behind lights what the picked row would leave.
  useEffect(() => {
    onPreview?.(picked?.kind ? { def: picked.kind.def || null, value: picked.value, of: picked.kind.of } : null);
  }, [picked, onPreview]);
  useEffect(() => () => onPreview?.(null), [onPreview]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-finder-row="${at}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [at]);

  const commit = (row, { out = false, start = false } = {}) => {
    if (row?.field) {
      setTerm(`${row.field}:`);
      setCursor(0);
      inputRef.current?.focus();
      return;
    }
    let next = query;
    if (row?.recent) next = row.recent;
    else if (row) {
      // Plain words are one phrase; a field and a value stand as typed.
      const clause = row.words && /\s/.test(row.raw) && !/^".*"$/.test(row.raw) ? `"${row.raw.replace(/"/g, "")}"` : row.raw ?? row.clause;
      next = appendClause(query, out || typed.leaveOut ? `-${clause.replace(/^-/, "").replace("!=", ":")}` : clause);
    }
    setQuery(next);
    setTerm("");
    setCursor(-1);
    if (start && (next.trim() || picks.length)) {
      remember(next);
      onStart?.(next);
      inputRef.current?.blur();
    }
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setCursor(Math.min(flat.length - 1, at + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor(at <= 0 ? (typed.raw ? 0 : -1) : at - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const start = e.metaKey || e.ctrlKey;
      // Cmd+Enter with nothing picked starts from the search as it stands.
      if (picked || start) commit(start && !typed.raw ? null : picked, { out: e.shiftKey, start });
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (term) setTerm("");
      else inputRef.current?.blur();
    } else if (e.key === "Backspace" && !term && picks.length) {
      e.preventDefault();
      togglePick(picks[picks.length - 1]);
    } else if (e.key === "Backspace" && !term && pills.length) {
      e.preventDefault();
      setQuery(without(pills.length - 1));
    }
  };

  let n = -1;
  // A box this narrow (the chat open beside a small pane) says less on its right.
  const wide = width >= 1180;
  const cell = "grid shrink-0 self-stretch place-items-center border-l border-[color:var(--line-soft)] px-[14px] text-[12px] transition-colors";
  return (
    <div className="relative" style={{ width }} data-board-finder="">
      <div
        className="flex min-w-0 items-center rounded-[2px] border transition-colors"
        style={{
          height: FINDER_BOX_H,
          // Only its frame on the board's ground, with no fill or lift of
          // its own; the frame a shade up while the box is in use.
          borderColor: open ? "color-mix(in srgb, var(--muted) 55%, transparent)" : "var(--line-strong)",
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget || e.target === pillsRef.current) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        <span className={`ml-[15px] mr-[11px] shrink-0 transition-colors ${open ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}><Glass /></span>
        <div ref={pillsRef} className="flex h-full min-w-0 flex-1 items-center gap-[6px] overflow-hidden pr-[10px]" data-finder-pills="">
          {folded ? (
            <button type="button" onClick={() => setListing((v) => (v === "clauses" ? null : "clauses"))} aria-expanded={listing === "clauses"} aria-label={`${folded} more in the search`} className={`${PILL} shrink-0 pr-[8px] text-[color:var(--ink)] hover:text-[color:var(--green)]`} data-finder-more="">
              {`+${folded}`}
            </button>
          ) : null}
          {pills.map((p, i) => (i < folded ? null : (
            // A short value is never cut; a long one gives way first.
            <span key={`${p.text}:${i}`} className={`${PILL} ${p.value.length > 16 ? "" : "shrink-0"}`} data-finder-pill="">
              {p.negated ? <span className="shrink-0 text-[color:var(--muted)]">not</span> : null}
              {p.field ? <span className="shrink-0 text-[color:var(--muted)]">{p.field}</span> : null}
              <span className="min-w-[24px] max-w-[240px] truncate text-[color:var(--ink)]">{p.value}</span>
              <button type="button" aria-label={`Take ${p.value} out of the search`} onClick={() => setQuery(without(i))} className={PILL_X}>&times;</button>
            </span>
          )))}
          {picks.length ? (
            <span className={PILL} data-finder-pick="">
              <span className="shrink-0 text-[color:var(--muted)]">{picks.length === 1 ? "trace" : "picked"}</span>
              {picks.length === 1 ? (
                <span className="min-w-[40px] max-w-[220px] truncate text-[color:var(--ink)]">{picks[0].title || `Trace ${String(picks[0].id).slice(0, 8)}`}</span>
              ) : (
                <button type="button" onClick={() => setListing((v) => (v === "picks" ? null : "picks"))} aria-expanded={listing === "picks"} className="min-w-0 truncate text-[color:var(--ink)] hover:text-[color:var(--green)]" data-finder-picks-open="">{`${picks.length} traces`}</button>
              )}
              <button type="button" aria-label={picks.length === 1 ? "Take the trace out of the search" : "Take the picked traces out of the search"} onClick={() => setPicks([])} className={PILL_X}>&times;</button>
            </span>
          ) : null}
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => { setTerm(e.target.value); setCursor(e.target.value.trim() ? 0 : -1); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => { setOpen(false); setTerm(""); setCursor(-1); remember(query); }}
            onKeyDown={onKey}
            placeholder={pills.length ? "Narrow further" : picks.length ? "Search, or pick more" : `${notice ? `${notice} ` : ""}Search the work: a person, an agent, a kind of work, an MCP server, a skill, a file...`}
            className="h-full min-w-[140px] flex-1 bg-transparent text-[14px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
            aria-label="Search the work"
            aria-expanded={open}
            aria-controls="board-finder-list"
            role="combobox"
            data-finder-input=""
          />
        </div>
        <span className="flex shrink-0 items-baseline gap-x-[12px] whitespace-nowrap pl-[6px] pr-[14px] text-[12px] tabular-nums" data-finder-reach="">
          <Reach reach={reach} reading={reading} picked={picked} total={total} wide={wide} />
        </span>
        {searched ? (
          <>
            <button type="button" onClick={() => commit(null, { start: true })} className={`${cell} text-[color:var(--ink)] hover:bg-[color:var(--gray-fill)]`} data-board-start="">{wide ? startLabel : startShort}</button>
            <button type="button" onClick={() => { setQuery(""); setTerm(""); setPicks([]); }} className={`${cell} text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)]`}>Clear</button>
          </>
        ) : null}
      </div>
      {listing ? (
        <div ref={listingRef} className="absolute left-0 z-[31] mt-[6px] flex max-h-[360px] w-[440px] flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-lg" data-finder-listing={listing}>
          <div className={`${EYEBROW_CLASS} px-[12px] pb-[2px] pt-[9px]`}>{listing === "picks" ? "Picked for the investigation" : "Also in the search"}</div>
          <div className="min-h-0 flex-1 overflow-y-auto pb-[4px]">
            {(listing === "picks"
              ? picks.map((x) => ({ key: x.id, name: x.title || `Trace ${String(x.id).slice(0, 8)}`, drop: () => togglePick(x) }))
              : pills.slice(0, folded).map((x, i) => ({ key: `${x.text}:${i}`, field: x.field, negated: x.negated, name: x.value, drop: () => setQuery(without(i)) }))
            ).map((x) => (
              <div key={x.key} className="flex items-center gap-[8px] px-[12px] text-[12px] leading-[28px]">
                <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">
                  {x.negated ? <span className="text-[color:var(--muted)]">{"not "}</span> : null}
                  {x.field ? <span className="text-[color:var(--muted)]">{`${x.field} `}</span> : null}
                  {x.name}
                </span>
                <button type="button" aria-label={`Take ${x.name} out of the search`} onClick={x.drop} className="shrink-0 text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">&times;</button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {open ? (
        <div
          className="absolute left-0 z-[30] mt-[6px] flex flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-lg"
          style={{ width: finderWidth, maxHeight: finderHeight }}
          onMouseDown={(e) => e.preventDefault()}
          data-finder-list=""
        >
          <div ref={listRef} id="board-finder-list" role="listbox" className="min-h-0 flex-1 overflow-y-auto pb-[4px]">
            {!typed.raw ? (
              <div className="flex flex-wrap items-center gap-[5px] border-b border-[color:var(--line-soft)] px-[12px] pb-[10px] pt-[10px]">
                <span className={`${EYEBROW_CLASS} basis-full pb-[2px]`}>Search by</span>
                {by.map(([word, says]) => (
                  <button key={word} type="button" aria-label={`Search by ${says}`} onClick={() => commit({ field: word })} className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] text-[11px] leading-[20px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]">{`${word}:`}</button>
                ))}
              </div>
            ) : null}
            {sections.map((sec) => (
              <div key={sec.label} role="group" aria-label={sec.label}>
                <div className={`${EYEBROW_CLASS} px-[12px] pb-[2px] pt-[10px]`}>{sec.label}</div>
                {sec.rows.map((row) => {
                  n += 1;
                  const i = n;
                  const on = i === at;
                  const counted = row.sessions != null;
                  return (
                    <button
                      key={row.key}
                      type="button"
                      role="option"
                      aria-selected={on}
                      onMouseMove={() => (i === at ? null : setCursor(i))}
                      onClick={(e) => commit(row, { out: e.shiftKey, start: e.metaKey || e.ctrlKey })}
                      className={`grid w-full items-center gap-x-[10px] px-[12px] text-left leading-[28px] ${on ? "bg-[color:var(--green-soft)]" : ""}`}
                      style={{ gridTemplateColumns: `${sec.mixed ? "52px " : ""}minmax(0,1fr) 60px 36px 44px 64px` }}
                      data-finder-row={i}
                    >
                      {sec.mixed ? <span className="truncate text-[11px] text-[color:var(--muted-soft)]">{row.kind.one}</span> : null}
                      <span className={`min-w-0 truncate text-[12px] ${on ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>
                        {row.field ? (
                          <><span className="text-[color:var(--ink)]">{row.name}</span>{` ${row.says}`}</>
                        ) : row.words ? (
                          <>{"Traces that mention "}<span className="text-[color:var(--ink)]">{row.name}</span></>
                        ) : row.raw != null || row.recent ? (
                          <span className="text-[color:var(--ink)]">{row.name}</span>
                        ) : (
                          <Highlight text={row.name} term={typed.find} />
                        )}
                      </span>
                      {counted ? <ShareBar row={row} max={max} /> : <span />}
                      <span className="text-right text-[11px] tabular-nums text-[color:var(--muted)]">{counted ? NUM.format(row.sessions) : ""}</span>
                      <span className="text-right text-[11px] tabular-nums" style={{ color: MET }}>{row.met ? `${NUM.format(row.met)} met` : ""}</span>
                      <span className="text-right text-[11px] tabular-nums" style={{ color: FLAGGED }}>{row.flagged ? `${NUM.format(row.flagged)} flagged` : ""}</span>
                    </button>
                  );
                })}
              </div>
            ))}
            {!flat.length && typed.raw ? (
              <p className="m-0 px-[12px] py-[12px] text-[12px] text-[color:var(--muted)]">
                {typed.key && !typed.find ? `Type a value to search by ${term.trim()}` : "Nothing in reach goes by that name."}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-x-[14px] gap-y-[4px] border-t border-[color:var(--line)] px-[12px] py-[7px] text-[11px] leading-[16px] text-[color:var(--muted)]">
            <span><Key>Enter</Key>narrow to it</span>
            <span><Key>Shift Enter</Key>leave it out</span>
            <span><Key>{`${MOD} Enter`}</Key>{startHint}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
